/**
 * Pure gating logic for the session-replay recorder — the "should this session be
 * recorded, and under which slug" decision + the consent check. Extracted from
 * entry.ts so it is unit-testable without rrweb or a DOM (see
 * scripts/check-replay-gate.ts). No side effects: the caller sets cookies.
 */

export const REC_COOKIE = "_mdb_rec"; // "1" sampled-in | "0" sampled-out (sticky per funnel session)
export const REC_SLUG_COOKIE = "_mdb_rec_slug"; // the replay-enabled LP's slug, so checkout posts under it
// Website pages roll their OWN sample under a separate cookie. Sharing _mdb_rec
// would let the two surfaces overwrite each other's decision: a website
// sampled-out visitor would arrive on a replay-enabled LP already carrying "0"
// and never be recorded, and a website sample-in would point _mdb_rec_slug at a
// host that is not a landing-page slug, so the funnel's downstream `cont` pages
// would post under a slug that resolves to nothing.
export const REC_SITE_COOKIE = "_mdb_rec_site";

/** Website surface: recordings post to host+path instead of a landing-page slug. */
export interface ReplaySiteSurface {
  host: string;
  path: string;
}

export interface ReplayConfig {
  slug: string;
  apiUrl: string;
  enabled?: boolean; // server already gated on settings.replay.enabled
  samplePct?: number; // 0..100 client sample; default 100
  cont?: boolean; // downstream funnel page: continue only an already-sampled session (LP only)
  gdpr?: boolean; // server-computed GDPR region, used when the _ms_gdpr cookie is absent
  site?: ReplaySiteSurface; // present → website page, not a landing page
}

export interface GateDecision {
  record: boolean;
  postSlug: string;
  cookiesToSet: { name: string; value: string }[];
}

/**
 * Decides whether to record and which slug to post under. Sticky per funnel
 * session: the sample is rolled once (on a replay-enabled LP) and stored in
 * _mdb_rec, so reloads and downstream pages stay consistent. Downstream pages
 * (cont) only continue a session an LP already sampled in.
 *
 * A WEBSITE page (cfg.site) is a separate, self-contained decision: its own
 * sticky cookie, no funnel continuation, and it never reads or writes the LP
 * cookies (see REC_SITE_COOKIE). `postSlug` carries the host so the caller has a
 * non-empty surface key; the site endpoint builds its URL from cfg.site.
 *
 * `rand` is injectable for tests; defaults to Math.random.
 */
export function decideRecording(
  cfg: ReplayConfig,
  getCookie: (n: string) => string,
  rand: () => number = Math.random
): GateDecision {
  let postSlug = cfg.slug || "";
  const cookiesToSet: { name: string; value: string }[] = [];

  if (cfg.site) {
    if (!cfg.enabled || !cfg.site.host) return { record: false, postSlug: "", cookiesToSet };
    const prior = getCookie(REC_SITE_COOKIE);
    let rec: boolean;
    if (prior === "1") rec = true;
    else if (prior === "0") rec = false;
    else {
      const pct = typeof cfg.samplePct === "number" ? cfg.samplePct : 100;
      rec = rand() * 100 < pct;
      cookiesToSet.push({ name: REC_SITE_COOKIE, value: rec ? "1" : "0" });
    }
    return { record: rec, postSlug: cfg.site.host, cookiesToSet };
  }

  if (cfg.cont) {
    if (getCookie(REC_COOKIE) !== "1") return { record: false, postSlug, cookiesToSet };
    postSlug = getCookie(REC_SLUG_COOKIE) || postSlug;
    return { record: !!postSlug, postSlug, cookiesToSet };
  }

  if (cfg.enabled) {
    const prior = getCookie(REC_COOKIE);
    let rec: boolean;
    if (prior === "1") rec = true;
    else if (prior === "0") rec = false;
    else {
      const pct = typeof cfg.samplePct === "number" ? cfg.samplePct : 100;
      rec = rand() * 100 < pct;
      cookiesToSet.push({ name: REC_COOKIE, value: rec ? "1" : "0" });
    }
    // (Re)point the slug at THIS replay-enabled LP on every sampled-in visit, not
    // just the first roll. The sample cookie now outlives a single funnel (2-day
    // max-age), so a buyer who later funnels through a DIFFERENT LP must have their
    // downstream checkout/upsell/success recordings attributed to the LP they
    // actually came from — otherwise a stale _mdb_rec_slug files them under an
    // earlier (possibly cross-tenant) landing page.
    if (rec && postSlug) cookiesToSet.push({ name: REC_SLUG_COOKIE, value: postSlug });
    return { record: rec && !!postSlug, postSlug, cookiesToSet };
  }

  return { record: false, postSlug, cookiesToSet };
}

/**
 * Strips the query string and fragment from a URL, keeping scheme://host/path.
 * Applied to the rrweb Meta event's href before buffering: rrweb records
 * location.href verbatim and maskAllInputs never touches URLs, so a checkout URL
 * like ?email=buyer@x.com would otherwise reach the stored chunk. Returns the
 * input unchanged if it carries no query/fragment.
 */
export function sanitizeHref(href: string): string {
  if (typeof href !== "string") return "";
  let cut = href.length;
  const qi = href.indexOf("?");
  const fi = href.indexOf("#");
  if (qi >= 0) cut = Math.min(cut, qi);
  if (fi >= 0) cut = Math.min(cut, fi);
  return href.slice(0, cut);
}

/**
 * The recorder's consent gate, identical in shape to the shared tag-loader gate
 * in lib/consent-region.ts. Restated rather than imported: this file is bundled
 * standalone by esbuild into public/mdb-replay.js, which ships on every funnel
 * page under a size budget, so it stays free of app-side imports.
 * scripts/check-replay-gate.ts pins these rules to the same fixtures.
 *
 * An explicit decline is sticky and beats the region: an EU visitor who declined
 * and later returns on a non-EU IP stays unrecorded. Otherwise: outside GDPR
 * record freely, inside GDPR only after consent. The `_ms_gdpr` cookie (stamped
 * per request by the middleware) wins; only '0' means confirmed non-GDPR, so
 * 'u' (no geo signal) and anything unrecognised fail closed. When the cookie is
 * absent entirely, fall back to the server-computed gdprHint and then to the
 * conservative default.
 */
export function consentAllowed(getCookie: (n: string) => string, gdprHint?: boolean): boolean {
  const c = getCookie("_ms_consent");
  if (c === "denied") return false;
  const cookie = getCookie("_ms_gdpr");
  const isGdpr = cookie !== "" ? cookie !== "0" : gdprHint !== undefined ? gdprHint : true;
  const granted = c === "granted" || c === "marketing";
  return !isGdpr || granted;
}
